import { showOpenCashDrawerModal } from 'src/cash-drawer/open-cash-drawer-modal';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import { ActivityType } from 'src/operation-notify/enum/activities.enum';
import { operationNotify } from 'src/operation-notify/operation-notify';
import { restorePage } from '../../common/components/emerald-home-page/emerald-home-page';
import { AuthorizationDomains } from '../../common/enumerations/authorization-domains';
import { CashOfficeUserActions } from '../../common/enumerations/cash-office-user-actions';
import { Denominations } from '../../common/interfaces/dtos/ITender';
import { store } from '../../common/redux/configure-store';
import { closeDrawer, openDrawer } from '../components/cash-drawer-actions/cash-drawer-actions';
import { displayPaidAccountSelectionView } from '../components/paid-account-selection/paid-account-selection';
import { ActiveTenderRequestOptions } from '../interfaces/ActiveTenderRequestOptions';
import { UserActions } from '../interfaces/UserActions';
import { processBlindPickup } from './blind-pickup';
import { getActiveTenders } from './get-active-tenders';
import { getTenderDenominations } from './get-tender-denominations';
import { PaidAccountType } from './services/get-paid-accounts';
import { ShowCashOfficeView } from './show-cash-office-view';
import { processTillClosing } from './till-closing';

const getCashOfficePaymentOptions = async (
  tenderRequestOptions: Partial<ActiveTenderRequestOptions>,
): Promise<void> => {
  return getActiveTenders(tenderRequestOptions)
    .then(getTenderDenominations)
    .then(denoms => {
      if (denoms?.denominationGroups?.length) {
        mapTenderDenomainations(denoms);
      }
    });
};

const mapTenderDenomainations = (denominations: Denominations): void => {
  for (const tenderGroup of store.getState().cashOffice.sellableTenders.tenderGroups) {
    if (tenderGroup.tenders?.length) {
      for (const tender of tenderGroup.tenders) {
        if (tender.isByDenomination && tender.currency?.isoSymbol) {
          const currencySymbol = tender.currency.isoSymbol;
          const denomList = denominations.denominationGroups.filter(
            denom => denom.currencySymbol.toLowerCase() === currencySymbol.toLowerCase(),
          );
          if (denomList?.length) {
            tender.denominations = denomList[0];
          }
        }
      }
    }
  }
};

const openTenderPickupActionView = async (): Promise<void> => {
  const pickupRequestOptions: Partial<ActiveTenderRequestOptions> = { pickup: true };
  return getCashOfficePaymentOptions(pickupRequestOptions)
    .then(openDrawerAtNonTrainingMode)
    .then(() => ShowCashOfficeView(CashOfficeUserActions.TenderPickup))
    .catch(popupMessage);
};

const showPaidAccountSelectionView = async (accountLookupType: PaidAccountType): Promise<void> => {
  const activityName: ActivityType =
    accountLookupType === PaidAccountType.PaidIn ? ActivityType.PaidIn : ActivityType.PaidOut;
  return operationNotify(activityName)
    .then((): void => {
      displayPaidAccountSelectionView(accountLookupType);
    })
    .catch(popupMessage);
};

const hidePaidAccountSelectionView = (): void => restorePage();

const openPaidOutActionWindow = async (accountName: string, accountId: string): Promise<void> => {
  const pickupRequestOptions: Partial<ActiveTenderRequestOptions> = { payOut: true };
  return getCashOfficePaymentOptions(pickupRequestOptions)
    .then(openDrawerAtNonTrainingMode)
    .then(() => ShowCashOfficeView(CashOfficeUserActions.PaidOut, accountName, accountId))
    .catch(popupMessage);
};

const openPaidInActionWindow = async (
  accountName: string,
  accountId: string,
  taxable: boolean,
): Promise<void> => {
  const pickupRequestOptions: Partial<ActiveTenderRequestOptions> = { payIn: true };
  return getCashOfficePaymentOptions(pickupRequestOptions)
    .then(openDrawerAtNonTrainingMode)
    .then(() => {
      ShowCashOfficeView(CashOfficeUserActions.PaidIn, accountName, accountId, taxable);
    })
    .catch(popupMessage);
};

const openDeclarationActionView = async (): Promise<void> => {
  const declarationRequestOptions: Partial<ActiveTenderRequestOptions> = { tillDeclaration: true };
  return getCashOfficePaymentOptions(declarationRequestOptions)
    .then(openDrawerAtNonTrainingMode)
    .then(() => ShowCashOfficeView(CashOfficeUserActions.TillDeclaration))
    .catch(popupMessage);
};

const openLoanAddActionView = async (): Promise<void> => {
  const loanAddRequestOptions: Partial<ActiveTenderRequestOptions> = { loan: true };
  return getCashOfficePaymentOptions(loanAddRequestOptions)
    .then(openDrawerAtNonTrainingMode)
    .then(() => ShowCashOfficeView(CashOfficeUserActions.LoanAdd))
    .catch(popupMessage);
};

const performTillClosing = async (): Promise<void> => {
  return operationNotify(ActivityType.TillClosing)
    .then(openDrawerAtNonTrainingMode)
    .then(processTillClosing)
    .catch(popupMessage);
};

export const openCashDrawer = async (): Promise<void> => {
  showOpenCashDrawerModal();
};

const handleBlindPickup = async (): Promise<void> => {
  const blindPickupRequestOptions: Partial<ActiveTenderRequestOptions> = { blindPickup: true };
  return getCashOfficePaymentOptions(blindPickupRequestOptions)
    .then(processBlindPickup)
    .then(openDrawerAtNonTrainingMode)
    .then(() => {
      closeDrawer();
    })
    .catch(popupMessage);
};

const cashOfficeAccess = (): UserActions => {
  const userActions = store.getState().user.userActions;
  const userCashOfficeActions: UserActions = {
    allActions: false,
    paidIn: false,
    paidOut: false,
    tenderPickup: false,
    tillDeclaration: false,
    loanAdd: false,
    tillClosing: false,
    blindPickup: false,
  };

  if (userActions) {
    for (const item of Object.values(userActions)) {
      const domainShortName = item.name.replace(/\s/g, '');
      const actionShortName = item.action.replace(/\s/g, '');
      if (
        domainShortName === AuthorizationDomains.Any &&
        actionShortName === CashOfficeUserActions.Any
      ) {
        userCashOfficeActions.allActions = true;
      } else if (
        domainShortName === AuthorizationDomains.CashOffice &&
        actionShortName === CashOfficeUserActions.Any
      ) {
        userCashOfficeActions.allActions = true;
      } else if (
        domainShortName === AuthorizationDomains.CashOffice &&
        actionShortName === CashOfficeUserActions.PaidIn
      ) {
        userCashOfficeActions.paidIn = true;
      } else if (
        domainShortName === AuthorizationDomains.CashOffice &&
        actionShortName === CashOfficeUserActions.PaidOut
      ) {
        userCashOfficeActions.paidOut = true;
      } else if (
        domainShortName === AuthorizationDomains.CashOffice &&
        actionShortName === CashOfficeUserActions.TenderPickup
      ) {
        userCashOfficeActions.tenderPickup = true;
      } else if (
        domainShortName === AuthorizationDomains.CashOffice &&
        actionShortName === CashOfficeUserActions.TillDeclaration
      ) {
        userCashOfficeActions.tillDeclaration = true;
      } else if (
        domainShortName === AuthorizationDomains.CashOffice &&
        actionShortName === CashOfficeUserActions.TillClosing
      ) {
        userCashOfficeActions.tillClosing = true;
      } else if (
        domainShortName === AuthorizationDomains.CashOffice &&
        actionShortName === CashOfficeUserActions.LoanAdd
      ) {
        userCashOfficeActions.loanAdd = true;
      } else if (
        domainShortName === AuthorizationDomains.CashOffice &&
        actionShortName === CashOfficeUserActions.BlindPickup
      ) {
        userCashOfficeActions.blindPickup = true;
      }
    }
  }
  return userCashOfficeActions;
};

export const HandleCashOfficeActions = {
  openTenderPickupActionView,
  showPaidAccountSelectionView,
  hidePaidAccountSelectionView,
  openPaidOutActionWindow,
  openDeclarationActionView,
  openLoanAddActionView,
  cashOfficeAccess,
  openPaidInActionWindow,
  performTillClosing,
  openCashDrawer,
  handleBlindPickup,
};

const openDrawerAtNonTrainingMode = async (): Promise<void> => {
  if (!store.getState().posMode.trainingMode) {
    await openDrawer();
  }
};
